import {isExpectedError} from '@/src/shared/utils/expected-error'
import * as cookie from 'cookie'
import {inject, injectable} from 'inversify'
import type {
  ApiRequestOptions,
  IApiClientService,
  IApiRequestBuilder,
  IApiResponseHandler,
} from '@/src/ports/services/api'
import {DI} from '@/src/config/di/types'
import type {CaptchaService} from '@/src/ports/services/captcha'
import {phpSessionCookieName} from '@/src/config/settings'
import type {ICurrentSessionResolver} from '@/src/ports/services/current-session-resolver'
import type {ISessionResponseProcessor} from '@/src/ports/services/session-response-processor'
import {isMobile, isWeb} from '@/src/shared/utils/functions'
import type {IInstrumentationService} from '@/src/ports/services/monitoring/instrumentation'
import type {ILoggerService} from '@/src/ports/services/logger'
import type {IErrorReporter} from '@/src/ports/services/monitoring'
import {
  getClientSessionLifecycle,
  pendingSessionRequest,
} from '@/src/infrastructure/session/client-session-lifecycle'
import * as Sentry from '@sentry/nextjs'

@injectable()
export class ApiClientService implements IApiClientService {
  @inject(DI.IApiRequestBuilder)
  requestBuilder!: IApiRequestBuilder

  @inject(DI.IApiResponseHandler)
  responseHandler!: IApiResponseHandler

  @inject(DI.IErrorReporter)
  errorReporter!: IErrorReporter

  @inject(DI.IInstrumentationService)
  instrumentation!: IInstrumentationService

  @inject(DI.CaptchaService)
  captchaService!: CaptchaService

  @inject(DI.ILoggerService)
  logger!: ILoggerService

  @inject(DI.ICurrentSessionResolver)
  currentSessionResolver!: ICurrentSessionResolver

  @inject(DI.ISessionResponseProcessor)
  sessionResponseProcessor!: ISessionResponseProcessor

  async get<ResponseType>(url: string, options?: ApiRequestOptions): Promise<ResponseType> {
    options = {...options, sessionGeneration: getClientSessionLifecycle()?.capture()}
    return await this.instrumentation.startSpanAsync(
      {
        name: `(api) GET [${(isWeb() && typeof window === 'undefined' && 'server') || 'client'}]: ${url}`,
        attributes: {
          url,
          options: JSON.stringify(options),
        },
      },
      async () => {
        const {url: finalUrl, options: finalOptions} = await this.requestBuilder.build(url, {
          ...options,
          method: 'GET',
        })

        return this._request(finalUrl, finalOptions)
      },
    )
  }

  async post<ResponseType>(url: string, options?: ApiRequestOptions): Promise<ResponseType> {
    options = {...options, sessionGeneration: getClientSessionLifecycle()?.capture()}
    return await this.instrumentation.startSpanAsync(
      {
        name: `(api) POST [${(isWeb() && typeof window === 'undefined' && 'server') || 'client'}]: ${url}`,
        attributes: {
          url,
          options: JSON.stringify(options),
        },
      },
      async () => {
        const {url: finalUrl, options: finalOptions} = await this.requestBuilder.build(url, {
          ...options,
          method: 'POST',
        })

        return await this._request(finalUrl, finalOptions)
      },
    )
  }

  async _request<ResponseType>(
    url: string,
    options: ApiRequestOptions = {},
  ): Promise<ResponseType> {
    const isServer = typeof window === 'undefined'
    const context = isServer ? 'server' : 'client'
    const lifecycle = getClientSessionLifecycle()
    options.sessionGeneration ??= lifecycle?.capture()
    if (lifecycle && !lifecycle.isCurrent(options.sessionGeneration!))
      return pendingSessionRequest()

    try {
      this.logger.debug(`API Request [${context}]`, {
        url,
        method: options.method,
        data: options.data,
        params: options.params,
        headers: options.headers,
      })

      if (!options.body && options.data) {
        if (options.data.captcha_token && isServer) {
          this.logger.debug('Verifying captcha token')
          if (!isMobile()) {
            await this.captchaService.verify(options.data.captcha_token)
          }
        }

        options.body = JSON.stringify(options.data)
      }

      const sessionResolution =
        isServer && !options.withoutSession
          ? await this.currentSessionResolver.resolve()
          : {session: null}

      const session = sessionResolution.session

      if (
        isServer &&
        session?.phpSessionId &&
        !options.withoutSession &&
        !options.headers?.Cookie
      ) {
        options.headers = {
          ...options.headers,
          Cookie: cookie.serialize(phpSessionCookieName, session.phpSessionId),
        }
      }

      Sentry.logger.warn(`Отправка запроса ${url}`, {options})

      const response = await fetch(url, options)

      this.logger.debug(`API Response [${context}]`, {
        url,
        status: response.status,
      })

      if (
        isServer &&
        !options.withoutSession &&
        !options.allowCookieMutation &&
        response.status === 401
      ) {
        return await this.responseHandler.redirect('/api/auth/reset-session/')
      }

      if (isServer && !options.withoutSession && options.allowCookieMutation) {
        const body = await response
          .clone()
          .json()
          .catch(() => null)

        const data = body?.success === true ? body.data : body

        const sessionResult = await this.sessionResponseProcessor.process({
          response,
          session,
          data,
          isMobile: options.isMobile ?? isMobile(),
          mobileCsrf: options.mobileCsrf,
        })

        if (options.onSessionProcessed) {
          options.onSessionProcessed(sessionResult)
        } else if (sessionResult.redirect) {
          return await this.responseHandler.redirect(sessionResult.redirect)
        }
      }

      return await this.responseHandler.handle<ResponseType>(response, options)
    } catch (e: unknown) {
      if (isExpectedError(e)) throw e
      this.logger.error(`API Error [${context}]`, e, {url, options})
      this.errorReporter.capture(e)
      throw e
    }
  }
}
